/**
 * App Update Checker for pyxl Desktop
 *
 * Uses Tauri's updater plugin to check for new versions on launch.
 * Only active in Tauri (desktop) environment.
 *
 * Flow:
 * 1. App launches → check for updates (after 3s delay)
 * 2. If update available → notify via callback with version + notes
 * 3. User confirms → download and install
 * 4. App restarts with new version
 */

import { isTauri } from './db/database';

export interface UpdateInfo {
  version: string;
  body: string; // Release notes (markdown)
  date: string | null;
}

export type UpdateStatus =
  | { type: 'idle' }
  | { type: 'checking' }
  | { type: 'available'; info: UpdateInfo }
  | { type: 'downloading'; progress: number }
  | { type: 'ready' } // Downloaded, restart to apply
  | { type: 'error'; message: string }
  | { type: 'up-to-date' };

type StatusListener = (status: UpdateStatus) => void;

let _status: UpdateStatus = { type: 'idle' };
const _listeners = new Set<StatusListener>();

function setStatus(status: UpdateStatus): void {
  _status = status;
  _listeners.forEach((fn) => fn(status));
}

/**
 * Get current update status.
 */
export function getUpdateStatus(): UpdateStatus {
  return _status;
}

/**
 * Subscribe to update status changes. Returns unsubscribe function.
 */
export function onUpdateStatusChange(listener: StatusListener): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/**
 * Check for updates. Called automatically on app launch.
 * Can also be called manually from settings.
 */
export async function checkForUpdate(): Promise<void> {
  if (!isTauri()) return;

  try {
    setStatus({ type: 'checking' });

    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();

    if (update) {
      setStatus({
        type: 'available',
        info: {
          version: update.version,
          body: update.body ?? 'No release notes available.',
          date: update.date ?? null,
        },
      });
    } else {
      setStatus({ type: 'up-to-date' });
      // Auto-dismiss after a few seconds
      setTimeout(() => {
        if (_status.type === 'up-to-date') {
          setStatus({ type: 'idle' });
        }
      }, 5000);
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn('[pyxl] Update check failed:', message);
    setStatus({ type: 'error', message });
    // Auto-dismiss errors (non-blocking)
    setTimeout(() => {
      if (_status.type === 'error') {
        setStatus({ type: 'idle' });
      }
    }, 10000);
  }
}

/**
 * Download and install the available update.
 * Requires an update to be available (status.type === 'available').
 */
export async function downloadAndInstall(): Promise<void> {
  if (!isTauri()) return;

  try {
    setStatus({ type: 'downloading', progress: 0 });

    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();

    if (!update) {
      setStatus({ type: 'up-to-date' });
      return;
    }

    // Download with progress tracking
    let downloaded = 0;
    let contentLength = 0;

    await update.downloadAndInstall((event) => {
      switch (event.event) {
        case 'Started':
          contentLength = event.data.contentLength ?? 0;
          break;
        case 'Progress':
          downloaded += event.data.chunkLength;
          const progress = contentLength > 0
            ? Math.round((downloaded / contentLength) * 100)
            : 0;
          setStatus({ type: 'downloading', progress });
          break;
        case 'Finished':
          setStatus({ type: 'ready' });
          break;
      }
    });

    // After install, the app should be restarted
    // Tauri handles the restart via relaunch
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[pyxl] Update install failed:', message);
    setStatus({ type: 'error', message });
  }
}

/**
 * Initialize auto-update checking on app launch.
 * Delayed to avoid impacting startup performance.
 */
export function initAutoUpdate(): void {
  if (!isTauri()) return;

  // Check after a 3 second delay to let the app settle
  setTimeout(() => {
    checkForUpdate();
  }, 3000);
}
